import React, { useEffect, useRef } from 'react'
import { useValues, useActions } from 'kea'
import { useMediaQuery } from 'react-responsive'
import PropTypes from 'prop-types'

import I18n from 'lib/i18n'

import Pagination from '~/scenes/search/search-page/pagination'
import NotFound from '~/scenes/search/search-page/not-found'
import sceneLogic from '~/scenes/search/logic'

import Result from './result'
import FakeResult from './fake-result'

import favoritesLogic from '../favourites-logic'

export default function Results ({ id }) {
  const { hoverResult, translateResultRequest, setResultsHeight } = useActions(sceneLogic)
  const {
    showMap,
    results,
    filter,
    favourites,
    hoverResultId,
    hoverLocationId,
    locale,
    dpr,
    isLoadingResults,
    isLoadingResultsRaw,
    resultsHeight
  } = useValues(sceneLogic)

  const { settingFavourite } = useValues(favoritesLogic)
  const { setFavourite, removeFavourite } = useActions(favoritesLogic)

  const isMobileView = useMediaQuery({
    query: '(max-width: 639px)'
  })

  const containerRef = useRef(null)

  useEffect(() => {
    if (isMobileView && !isLoadingResults && containerRef.current) {
      const height = containerRef.current.offsetHeight
      if (!resultsHeight || height > resultsHeight) {
        setResultsHeight(height)
      }
    }
  }, [isLoadingResults, results.results, isMobileView, resultsHeight, setResultsHeight, isLoadingResultsRaw])

  // --- Loading state ---
  if (isLoadingResults || isLoadingResultsRaw) {
    const count = results.per_page || 25

    return (
      <div id={id} className={`search-results ${showMap ? 'col-2-3' : 'col-1-1'}`} ref={containerRef}>
        {isMobileView ? (
          <div
            style={{
              height: resultsHeight ? `${resultsHeight}px` : 'auto'
            }}
            className='loading-container'
          >
            {Array.from({ length: count }, (_, index) => (
              <FakeResult key={`fake-result-${index}`} />
            ))}
          </div>
        ) : (
          Array.from({ length: count }, (_, index) => <FakeResult key={`fake-result-${index}`} />)
        )}
      </div>
    )
  }

  // --- No results ---
  if (!(results.total > 0 || results.online_teachers_section)) {
    return (
      <div id={id} className={`search-results ${showMap ? 'col-2-3' : 'col-1-1'}`} ref={containerRef}>
        <NotFound bounds={filter.bounds} />
      </div>
    )
  }

  // --- Actual results ---
  const isWebcamCourse = filter.location_types === 'webcam'
  const showFullLocation = isWebcamCourse || filter.favourites
  const offset = (results.page - 1) * results.per_page
  const fuzzyWarningIndex = filter.favourites ? -1 : results.direct - offset

  return (
    <div id={id} className={`search-results ${showMap ? 'col-2-3' : 'col-1-1'}`} ref={containerRef}>
      {results.results.map((result, index) => {
        const afterFuzzyWarning = fuzzyWarningIndex !== -1 && index >= fuzzyWarningIndex
        const isHovering =
          hoverResultId === result.id ||
          (hoverLocationId && result.locations.some(location => location.id === hoverLocationId))

        return (
          <React.Fragment key={`result-fragment-${result.id}`}>
            {index === fuzzyWarningIndex && (
              <div key='fuzzy-warning' className='fuzzy-warning'>
                {results.page === 1 && index === 0
                  ? I18n.t('main.index.extra_webcam_no_local')
                  : I18n.t('main.index.extra_webcam')}
              </div>
            )}

            <Result
              key={`result-${result.id}`}
              center={filter.center}
              dpr={dpr}
              hoverResult={hoverResult}
              isFavorite={favourites.includes(result.id)}
              isHovering={isHovering}
              locale={locale}
              removeFavorite={removeFavourite}
              result={result}
              score={result.score}
              setFavorite={setFavourite}
              settingFavorite={settingFavourite === result.id}
              showFullLocation={showFullLocation || afterFuzzyWarning}
              translateResultRequest={translateResultRequest}
            />
          </React.Fragment>
        )
      })}

      {filter.favourites ? <div className='favorite-header' key='favoriteHeader' /> : <Pagination key='pagination' />}
    </div>
  )
}

Results.propTypes = {
  id: PropTypes.string
}
